import { setTimeout } from "timers";

import classNames from "classnames";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { connect, ConnectedProps } from "react-redux";
import { RouteChildrenProps } from "react-router-dom";

import { fixedLength } from "@/helper/validator";
import {
  postCodeForLogin,
  postEmailForLogin,
} from "@/reducers/mrgReducer/mrgThunks";

import useRecaptcha from "../../../hooks/useRecaptcha/useRecaptcha";
import NumberFields from "../../General/InputFields/NumberFields";

import SuccessLoginModal from "./Feature/SuccessLoginModal";
import { AxiosError } from "axios";

const fixedSixLength = fixedLength(6);

type OwnProps = {
  goPrevPage: () => void;
  goNextPage: () => void;
};

type ReduxProps = ConnectedProps<typeof connector>;

type CombineProps = OwnProps & ReduxProps & RouteChildrenProps;

type FormValues = {
  code: string;
};

type Props = CombineProps;

const PageTwo: React.FC<Props> = ({
  goPrevPage,
  goNextPage,
  email,
  postCodeForLogin,
  postEmailForLogin,
}) => {
  const {
    formState: { isValid, touched },
    errors,
    setError,
    control,
    handleSubmit,
  } = useForm<FormValues>({
    mode: "onChange",
  });

  const { gRecaptchaToken } = useRecaptcha();
  const [showModal, setShowModal] = useState<boolean>(false);

  const onSubmit = async (formValue: FormValues) => {
    try {
      await postCodeForLogin(formValue.code);
      setShowModal(true);
      await new Promise((res) => setTimeout(() => res("success"), 1500));
      setShowModal(false);
      goNextPage();
    } catch (error) {
      const data = (error as AxiosError)?.response?.data;
      let message = "驗證碼錯誤。";

      if (data) message = data.messages?.[0];

      setError("code", {
        type: "validate",
        message,
      });
    }
  };

  const onResendClick = async () => {
    try {
      const token = await gRecaptchaToken();
      postEmailForLogin(email, token);
    } catch (error) {
      return;
    }
  };

  return (
    <form className="login__page flex-1">
      {showModal && <SuccessLoginModal />}
      <div className="login__window">
        <h2 className="heading__2 login__window--heading">輸入驗證碼</h2>
        <p className="body login__window--body login__window--body__code">
          驗證碼已寄送至
          <span className="login__window--email">
            {email}
            <button
              type={"button"}
              onClick={goPrevPage}
              className="login__window--change_email tiny"
            >
              更改信箱
            </button>
          </span>
        </p>
        <Controller
          name="code"
          control={control}
          render={(props) => (
            <NumberFields
              error={errors.code?.message}
              touched={touched.code}
              label="驗證碼"
              resend
              resendCb={onResendClick}
              maxLength={6}
              minLength={6}
              inputMode="numeric"
              {...props}
            />
          )}
          rules={{
            required: true,
            validate: fixedSixLength,
            maxLength: 6,
            minLength: 6,
          }}
          defaultValue={""}
        />
        <button
          type={"submit"}
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid}
          className="login__btn btn btn__primary"
        >
          登入
        </button>
        <div className={classNames("my-3")}>
          <p
            className={classNames(
              "mx-auto",
              "w-[16rem]",
              "text-grey-3 leading-4 text-[0.75rem]"
            )}
          >
            This site is protected by reCAPTCHA and the Google
            <a
              target={"_blank"}
              rel={"noreferrer noopener"}
              className={classNames("text-primary-pink mx-1")}
              href="https://policies.google.com/privacy"
            >
              Privacy Policy
            </a>{" "}
            and
            <a
              target={"_blank"}
              rel={"noreferrer noopener"}
              className={classNames("text-primary-pink mx-1")}
              href="https://policies.google.com/terms"
            >
              Terms of Service
            </a>{" "}
            apply.
          </p>
        </div>
        <p className="tiny login__apply">
          還不是會員嗎？{" "}
          <a
            href="https://hotcake.app"
            target="_blank"
            rel="noopener noreferrer"
            className="login__apply--anchor"
          >
            立即申請試用
          </a>
        </p>
      </div>
    </form>
  );
};

const mapStateToProps = (state: RootState) => ({
  email: state.mrg.email,
});

const mapDispatchToProps = { postCodeForLogin, postEmailForLogin };

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(PageTwo);
